import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Designation.css';
import { usePortalMenuPosition, useOutsideClose } from '../../../../../hooks/usePortalDropdown';

// --- Configuration ---
// Retrieve the base URL from the .env file for centralized configuration.
const API_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;

// --- API Endpoints ---
// Construct full endpoint paths from the base URL.
const CREATE_DESIGNATION_ENDPOINT = `${API_BASE_URL}/api/create-designation`;
const ASSIGN_DESIGNATION_ENDPOINT = `${API_BASE_URL}/api/assign-designation`;
const GET_USERS_ENDPOINT = `${API_BASE_URL}/api/get-users`;
const GET_DESIGNATIONS_ENDPOINT = `${API_BASE_URL}/api/get-designation`;

const AUTH_TOKEN_KEY = 'zTaskAuthToken';
const USER_DETAILS_KEY = 'zTaskUserDetails';

/**
 * A reusable dropdown component with search and clear functionality.
 */
const SearchableDropdown = ({
    options,
    value,
    onChange,
    placeholder,
    labelKey,
    valueKey,
    disabled,
    isLoading,
    renderOption,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const { menuStyle } = usePortalMenuPosition(isOpen, dropdownRef);
    useOutsideClose(isOpen, setIsOpen, dropdownRef, menuRef, () => setSearchTerm(''));

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        const lowercasedTerm = searchTerm.toLowerCase();
        return options.filter(option => {
            const displayLabel = renderOption ? renderOption(option) : (option[labelKey] || '');
            return displayLabel.toLowerCase().includes(lowercasedTerm);
        });
    }, [options, searchTerm, labelKey, renderOption]);

    const selectedOption = useMemo(() => {
        return options.find(option => String(option[valueKey]) === String(value));
    }, [options, value, valueKey]);

    const handleSelect = (option) => {
        onChange(option ? option[valueKey] : '');
        setIsOpen(false);
        setSearchTerm('');
    };

    const toggleDropdown = () => {
        if (!disabled && !isLoading) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className={`searchable-dropdown-container${isOpen ? ' is-open' : ''}`} ref={dropdownRef}>
            <div
                className={`dropdown-header ${disabled ? 'disabled' : ''} ${isLoading ? 'loading' : ''}${isOpen ? ' open' : ''}`}
                onClick={toggleDropdown}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => e.key === 'Enter' && toggleDropdown()}
            >
                {selectedOption ? (
                    <span className="selected-value">{renderOption ? renderOption(selectedOption) : selectedOption[labelKey]}</span>
                ) : (
                    <span className="placeholder">{placeholder}</span>
                )}
                <div className="dropdown-controls">
                    <span className="dropdown-arrow"></span>
                </div>
            </div>
            {isOpen && createPortal(
                <div className="dropdown-list-wrapper ui-portal-dropdown-menu" style={menuStyle} ref={menuRef}>
                    <div className="dropdown-search-container ui-portal-dropdown-search">
                        <input
                            type="text"
                            className="dropdown-search-input"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                         <span className="search-icon" />
                    </div>
                    <ul className="dropdown-list">
                        {!searchTerm && (
                             <li
                                className="dropdown-list-clear-item"
                                onClick={() => handleSelect(null)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSelect(null)}
                                tabIndex={0}
                            >
                                — Clear Selection —
                            </li>
                        )}
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <li key={option[valueKey]} onClick={() => handleSelect(option)} onKeyDown={(e) => e.key === 'Enter' && handleSelect(option)} tabIndex={0}>
                                    {renderOption ? renderOption(option) : option[labelKey]}
                                </li>
                            ))
                        ) : (
                            searchTerm && <li className="no-options">No results found</li>
                        )}
                    </ul>
                </div>,
                document.body
            )}
        </div>
    );
};

const Designation = ({ onProfileUpdate }) => {
    // --- State Management ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newDesignationName, setNewDesignationName] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedDesignationId, setSelectedDesignationId] = useState('');
    
    // Data states
    const [users, setUsers] = useState([]);
    const [designations, setDesignations] = useState([]);

    // UI Feedback states
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFetchingData, setIsFetchingData] = useState(true);
    const [notification, setNotification] = useState({ message: '', type: '' });
    
    const [confirmationState, setConfirmationState] = useState({
        isOpen: false,
        message: '',
        onConfirm: () => {},
        confirmButtonText: 'Confirm'
    });

    const fetchInitialData = useCallback(async () => {
        setIsFetchingData(true);
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) {
            showNotification('Authentication error. Please log in again.', 'error');
            setIsFetchingData(false);
            return;
        }

        try {
            const [usersResponse, designationsResponse] = await Promise.all([
                fetch(GET_USERS_ENDPOINT, { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(GET_DESIGNATIONS_ENDPOINT, { headers: { 'Authorization': `Bearer ${token}` } })
            ]);

            if (!usersResponse.ok) throw new Error(`Failed to fetch users: ${usersResponse.statusText}`);
            const usersData = await usersResponse.json();
            // Ensure IDs are strings for the dropdown component
            setUsers(usersData.userData ? usersData.userData.map(u => ({ ...u, id: String(u.id) })) : []);


            if (!designationsResponse.ok) throw new Error(`Failed to fetch designations: ${designationsResponse.statusText}`);
            const designationsData = await designationsResponse.json();
             // Ensure IDs are strings for the dropdown component
            setDesignations(designationsData.designations ? designationsData.designations.map(d => ({ ...d, designationId: String(d.designationId) })) : []);

        } catch (error) {
            console.error("Fetch data error:", error);
            showNotification(error.message || 'Failed to load page data.', 'error');
        } finally {
            setIsFetchingData(false);
        }
    }, []);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const showNotification = (message, type) => {
        setNotification({ message, type });
        setTimeout(() => setNotification({ message: '', type: '' }), 4000);
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => {
        if (isSubmitting) return;
        setIsModalOpen(false);
        setNewDesignationName('');
    };
    
    const handleCloseConfirmation = () => {
        if (isSubmitting) return;
        setConfirmationState({ isOpen: false, message: '', onConfirm: () => {}, confirmButtonText: 'Confirm' });
    };

    const handleConfirmAction = async () => {
        await confirmationState.onConfirm();
        handleCloseConfirmation();
    };
    
    const performCreateDesignation = async () => {
        setIsSubmitting(true);
        try {
            const url = new URL(CREATE_DESIGNATION_ENDPOINT);
            url.searchParams.append('name', newDesignationName);

            const response = await fetch(url.toString(), { 
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}`, 'Accept': 'application/json' }
            });

            if (response.ok) {
                const result = await response.json();
                const newDesignation = {
                    ...result.designation,
                    designationId: String(result.designation.designationId) // Ensure new designation also has a string ID
                };
                showNotification(`Designation "${newDesignation.designationName}" created successfully!`, 'success');
                setDesignations(prev => [...prev, newDesignation]);
                handleCloseModal();
            } else {
                const errorData = await response.json().catch(() => ({}));
                showNotification(errorData.message || 'Failed to create designation.', 'error');
            }
        } catch (error) {
            showNotification('An error occurred. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreateDesignation = (e) => {
        e.preventDefault();
        if (!newDesignationName.trim()) {
            showNotification('Designation name cannot be empty.', 'error');
            return;
        }

        setConfirmationState({
            isOpen: true,
            message: `Are you sure you want to create the designation "${newDesignationName.trim()}"?`,
            onConfirm: performCreateDesignation,
            confirmButtonText: 'Yes, Create It'
        });
    };

    const performAssignDesignation = async () => {
        setIsSubmitting(true);
        try {
            const url = new URL(ASSIGN_DESIGNATION_ENDPOINT);
            url.searchParams.append('userId', selectedUserId);
            url.searchParams.append('designationId', selectedDesignationId);

            const response = await fetch(url.toString(), { 
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` }
             });

            if (response.ok) {
                showNotification('Designation assigned successfully!', 'success');
                
                try {
                    const loggedInUserDetailsString = localStorage.getItem(USER_DETAILS_KEY);
                    if (loggedInUserDetailsString) {
                        const loggedInUserDetails = JSON.parse(loggedInUserDetailsString);
                        const updatedUser = users.find(user => user.id === selectedUserId); // Direct string comparison

                        if (updatedUser && updatedUser.email === loggedInUserDetails.email) {
                            console.log("Sidebar refresh triggered: Logged-in user's designation was changed.");
                            if (onProfileUpdate) {
                                onProfileUpdate();
                            }
                        }
                    }
                } catch (e) {
                    console.error("Failed to check for self-update to trigger sidebar refresh:", e);
                }

                setSelectedUserId('');
                setSelectedDesignationId('');

            } else {
                const errorData = await response.json().catch(() => ({}));
                showNotification(errorData.message || 'Failed to assign designation.', 'error');
            }
        } catch (error) {
            showNotification('An error occurred. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAssignDesignation = (e) => {
        e.preventDefault();
        if (!selectedUserId || !selectedDesignationId) {
            showNotification('Please select both a user and a designation.', 'error');
            return;
        }
        
        const userName = users.find(u => u.id === selectedUserId)?.name || 'the selected user';
        const designationName = designations.find(d => d.designationId === selectedDesignationId)?.designationName || 'the selected designation';
        
        setConfirmationState({
            isOpen: true,
            message: `Are you sure you want to assign "${designationName}" to ${userName}?`,
            onConfirm: performAssignDesignation,
            confirmButtonText: 'Yes, Assign It'
        });
    };
    
    const userPlaceholderText = isFetchingData
        ? "Loading users..."
        : (users.length === 0 ? "No users found" : "-- Select a User --");

    const designationPlaceholderText = isFetchingData
        ? "Loading designations..."
        : (designations.length === 0 ? "No designations found" : "-- Select a Designation --");

    return (
        <div className="admin-page-container">
            {notification.message && (
                <div className={`notification ${notification.type}`}>{notification.message}</div>
            )}

            {isModalOpen && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <form onSubmit={handleCreateDesignation}>
                            <div className="modal-header">
                                <h3 className="modal-title">Create New Designation</h3>
                            </div>
                            <div className="modal-body">
                                <label htmlFor="newDesignationName" className="form-label">Designation Name <span className="required-indicator">*</span></label>
                                <input
                                    id="newDesignationName"
                                    type="text"
                                    className="form-input"
                                    value={newDesignationName}
                                    onChange={(e) => setNewDesignationName(e.target.value)}
                                    placeholder="e.g., Senior Developer"
                                    disabled={isSubmitting}
                                    autoFocus
                                    required
                                />
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="button-secondary" onClick={handleCloseModal} disabled={isSubmitting}>
                                    Cancel
                                </button>
                                <button type="submit" className="button-primary" disabled={isSubmitting}>
                                    Create Designation
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {confirmationState.isOpen && (
                <div className="modal-overlay" onClick={handleCloseConfirmation}>
                    <div className="modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">Confirm Action</h3>
                        </div>
                        <div className="modal-body">
                            <p className="confirmation-text">{confirmationState.message}</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="button-secondary" onClick={handleCloseConfirmation} disabled={isSubmitting}>
                                Cancel
                            </button>
                            <button type="button" className="submit-button green" onClick={handleConfirmAction} disabled={isSubmitting}>
                                {isSubmitting ? 'Processing...' : confirmationState.confirmButtonText}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <header className="admin-page-header with-action">
                <div>
                    <h2 className="admin-page-title">Manage User Designations</h2>
                    <p className="admin-page-subtitle">Assign existing designations to users.</p>
                </div>
                <button className="button-primary" onClick={handleOpenModal}>
                    + Create Designation
                </button>
            </header>

            <div className="content-card">
                <form onSubmit={handleAssignDesignation}>
                    <h3 className="card-title">Assign Designation to User</h3>
                    <div className="form-group">
                        <label htmlFor="selectUser" className="form-label">Select User *</label>
                        <SearchableDropdown
                            options={users}
                            value={selectedUserId}
                            onChange={setSelectedUserId}
                            placeholder={userPlaceholderText}
                            labelKey="name"
                            valueKey="id"
                            disabled={isFetchingData || isSubmitting || users.length === 0}
                            isLoading={isFetchingData}
                            renderOption={(user) => `${user.name} (${user.email})`}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="selectDesignation" className="form-label">Select Designation *</label>
                        <SearchableDropdown
                            options={designations}
                            value={selectedDesignationId}
                            onChange={setSelectedDesignationId}
                            placeholder={designationPlaceholderText}
                            labelKey="designationName"
                            valueKey="designationId"
                            disabled={isFetchingData || isSubmitting || designations.length === 0}
                            isLoading={isFetchingData}
                        />
                    </div>
                    <button type="submit" className="submit-button green" disabled={isSubmitting || isFetchingData}>
                        {isSubmitting ? 'Assigning...' : 'Assign Designation to User'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Designation;